import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Chip, Avatar } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";
import TranslateIcon from "@mui/icons-material/Translate";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";

const token = () => localStorage.getItem("biyz_token");

const SECTION_META = {
  vocabulary: { label: "Kelime Çalışmaları", icon: "📖", color: "#4F7CFF" },
  grammar:    { label: "Gramer Çalışmaları", icon: "📐", color: "#8B6CFF" },
  speaking:   { label: "Sesli Konuşma Pratikleri", icon: "🗣️", color: "#10B981" },
  exercise:   { label: "Alıştırmalar", icon: "✍️", color: "#34D2E8" },
  test:       { label: "Testler", icon: "📝", color: "#F59E0B" },
};

function Ring({ percent, size = 130 }) {
  const r = (size - 16) / 2, c = 2 * Math.PI * r;
  const off = c - (percent / 100) * c;
  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        <defs>
          <linearGradient id="ringg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#34D2E8" /><stop offset="50%" stopColor="#4F7CFF" /><stop offset="100%" stopColor="#8B6CFF" />
          </linearGradient>
        </defs>
        <circle cx={size/2} cy={size/2} r={r} stroke="rgba(120,140,200,0.16)" strokeWidth="11" fill="none" />
        <circle cx={size/2} cy={size/2} r={r} stroke="url(#ringg)" strokeWidth="11" fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: 30, fontWeight: 800, color: "#1a2238", lineHeight: 1 }}>%{percent}</Typography>
        <Typography sx={{ fontSize: 11, color: "#5B6B8C", fontWeight: 600 }}>tamamlandı</Typography>
      </Box>
    </Box>
  );
}

function StatCard({ icon, value, label, color }) {
  return (
    <Box sx={{ flex: 1, minWidth: 120, background: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)",
      border: "1px solid rgba(120,140,200,0.16)", borderRadius: 3, p: 2, textAlign: "center" }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#1a2238", lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: "#5B6B8C", fontWeight: 600, mt: 0.5 }}>{label}</Typography>
    </Box>
  );
}

function SkillBar({ label, value, color }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1a2238" }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>%{value}</Typography>
      </Box>
      <Box sx={{ height: 9, borderRadius: 6, background: "rgba(120,140,200,0.15)", overflow: "hidden" }}>
        <Box sx={{ width: `${value}%`, height: "100%", borderRadius: 6, background: color, transition: "width 1s ease" }} />
      </Box>
    </Box>
  );
}

function ExamView({ data }) {
  const sc = { reading:"#4F7CFF", listening:"#8B6CFF", speaking:"#10B981", writing:"#F59E0B",
    vocabulary:"#4F7CFF", grammar:"#8B6CFF", clozetest:"#34D2E8", translation:"#F59E0B" };
  const sections = data.examSections || [];
  const scores = data.examScores || {};
  const mocks = data.mockHistory || [];
  return (
    <>
      <Box sx={{ background: "linear-gradient(135deg,rgba(79,124,255,0.1),rgba(139,108,255,0.1))",
        border: "1px solid rgba(79,124,255,0.25)", borderRadius: 4, p: 2.5, mb: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: "#1a2238" }}>📝 {data.exam} Hazırlık</Typography>
            <Typography variant="caption" color="text.secondary">
              Puan: {data.examScoreScale || ""} · Hedef: <b>{data.examTargetScore || "belirlenmedi"}</b>
              {data.examWeakSection ? ` · En zayıf: ${data.examWeakSection}` : ""}
            </Typography>
          </Box>
          <Chip label={data.diagnosticDone ? "✓ Giriş denemesi yapıldı" : "Giriş denemesi bekliyor"} size="small"
            sx={{ bgcolor: data.diagnosticDone ? "#dcfce7" : "#fef3c7", color: data.diagnosticDone ? "#15803d" : "#b45309", fontWeight: 700, height: 24 }} />
        </Box>
        {sections.map(s => {
          const v = scores[s.key];
          return (
            <Box key={s.key} sx={{ mb: 1.2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1a2238" }}>{s.name}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: sc[s.key] || "#4F7CFF" }}>{v != null ? v : "—"}</Typography>
              </Box>
              <Box sx={{ height: 9, borderRadius: 6, background: "rgba(120,140,200,0.15)", overflow: "hidden" }}>
                <Box sx={{ width: `${v != null ? Math.min(100, v) : 0}%`, height: "100%", borderRadius: 6, background: sc[s.key] || "#4F7CFF", transition: "width 1s" }} />
              </Box>
            </Box>
          );
        })}
        {scores.total != null && <Typography sx={{ mt: 1, fontWeight: 800, color: "#1a2238" }}>Toplam tahmini skor: {scores.total}</Typography>}
        {sections.length > 0 && Object.keys(scores).length === 0 &&
          <Typography variant="caption" color="text.secondary">Henüz skor yok — koçunla bir giriş denemesi yap, skorların burada görünsün.</Typography>}
      </Box>
      {mocks.length > 0 && (
        <Box sx={{ background: "rgba(255,255,255,0.72)", border: "1px solid rgba(120,140,200,0.16)", borderRadius: 4, p: 2.5, mb: 2.5 }}>
          <Typography fontWeight={700} sx={{ mb: 1.5, color: "#1a2238" }}>📊 Deneme Sınavı Geçmişin</Typography>
          {mocks.map((mk, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.6, borderBottom: "1px solid rgba(120,140,200,0.1)" }}>
              <Typography sx={{ fontSize: 12.5, color: "#5B6B8C" }}>{mk.date ? String(mk.date).slice(0, 10) : "-"}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#4F7CFF" }}>Toplam: {mk.total != null ? mk.total : "?"}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}

export default function ProgressPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gami, setGami] = useState(null);          // seri/XP/lig durumu
  const [board, setBoard] = useState(null);        // haftalik lig siralamasi
  const [boardScope, setBoardScope] = useState("global"); // global | friends

  useEffect(() => {
    fetch("/api/platform/chat/progress", { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => { setData(d?.ok ? d : null); setLoading(false); })
      .catch(() => setLoading(false));
    fetch("/api/platform/gamification/me", { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => { if (d?.ok) setGami(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`/api/platform/gamification/leaderboard?scope=${boardScope}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => { if (d?.ok) setBoard(d); }).catch(() => {});
  }, [boardScope]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}><CircularProgress /></Box>;
  if (!data) return (
    <Box sx={{ textAlign: "center", mt: 10, px: 3 }}>
      <SchoolIcon sx={{ fontSize: 56, color: "#4F7CFF", opacity: 0.5 }} />
      <Typography variant="h6" sx={{ mt: 1 }}>Henüz ilerleme verisi yok</Typography>
      <Typography color="text.secondary">Bir eğitmenle derse başla, ilerlemen burada görünecek.</Typography>
    </Box>
  );

  const cu = data.currentUnit;
  const log = data.log || [];
  const sections = ["vocabulary", "grammar", "speaking", "exercise", "test"];

  return (
    <Box sx={{ maxWidth: 940, mx: "auto", px: { xs: 1.5, sm: 3 }, py: 3, width: "100%", boxSizing: "border-box" }}>
      <Typography variant="h5" fontWeight={800} sx={{ color: "#1a2238", mb: 0.5 }}>İlerleme Panelin 📈</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {data.bot?.name}{!data.isExam && <> · CEFR Seviye: <b>{data.cefrLevel || cu?.level || "—"}</b></>}
      </Typography>

      {/* SINAV botu: exam paneli */}
      {data.isExam && <ExamView data={data} />}

      {/* Dil botu: ring + mevcut ünite */}
      {!data.isExam && (
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", alignItems: "center",
        background: "rgba(255,255,255,0.72)", backdropFilter: "blur(10px)", border: "1px solid rgba(120,140,200,0.16)",
        borderRadius: 4, p: 2.5, mb: 2.5 }}>
        <Ring percent={data.overallPercent} />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Chip label={`Şu an: ${cu?.level || "—"} · Ünite ${cu?.unitNo || "—"}`} size="small"
            sx={{ bgcolor: "#eef3ff", color: "#4F7CFF", fontWeight: 700, mb: 1 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: "#1a2238" }}>{cu?.title || "Başlamaya hazır"}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>🎯 {cu?.canDo || ""}</Typography>
          <Typography variant="caption" sx={{ color: "#5B6B8C", mt: 1, display: "block" }}>
            {data.completedUnits}/{data.totalUnits} ünite tamamlandı
          </Typography>
        </Box>
      </Box>
      )}

      {/* İstatistik kartları */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
        <StatCard icon={<LocalFireDepartmentIcon sx={{ fontSize: 30 }} />} value={gami?.streakCount ?? data.streak} label="Gün serisi" color="#F59E0B" />
        <StatCard icon={<EmojiEventsIcon sx={{ fontSize: 30 }} />} value={gami?.xpTotal ?? data.xp} label="XP puanı" color="#8B6CFF" />
        <StatCard icon={<SchoolIcon sx={{ fontSize: 30 }} />} value={data.lessonsCompleted} label="Tamamlanan ders" color="#4F7CFF" />
        <StatCard icon={<MenuBookIcon sx={{ fontSize: 30 }} />} value={data.vocab?.learning || 0} label="Öğrenilen kelime" color="#10B981" />
      </Box>

      {/* Günlük hedef + Haftalık Lig */}
      {gami && (
        <Box sx={{ background: "rgba(255,255,255,0.72)", backdropFilter: "blur(10px)", border: "1px solid rgba(120,140,200,0.16)", borderRadius: 4, p: 2.5, mb: 2.5 }}>
          {/* Günlük hedef çubuğu */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
            <Typography fontWeight={700} sx={{ color: "#1a2238" }}>🎯 Günlük Hedef</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: (gami.xpToday >= gami.dailyGoalXp) ? "#10B981" : "#8B6CFF" }}>
              {gami.xpToday || 0} / {gami.dailyGoalXp || 30} XP {gami.xpToday >= gami.dailyGoalXp ? "✓" : ""}
            </Typography>
          </Box>
          <Box sx={{ height: 10, borderRadius: 6, background: "rgba(120,140,200,0.15)", overflow: "hidden", mb: 2 }}>
            <Box sx={{ width: `${Math.min(100, Math.round(((gami.xpToday || 0) / (gami.dailyGoalXp || 30)) * 100))}%`,
              height: "100%", borderRadius: 6, background: "linear-gradient(90deg,#10B981,#34D2E8)", transition: "width 1s ease" }} />
          </Box>

          {/* Haftalık Lig */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.2 }}>
            <Typography fontWeight={700} sx={{ color: "#1a2238" }}>🏆 Haftalık Lig</Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Chip label="Herkes" size="small" onClick={() => setBoardScope("global")}
                sx={{ cursor: "pointer", fontWeight: 700, bgcolor: boardScope === "global" ? "#4F7CFF" : "rgba(120,140,200,0.15)", color: boardScope === "global" ? "#fff" : "#5B6B8C" }} />
              <Chip label="Arkadaşlar" size="small" onClick={() => setBoardScope("friends")}
                sx={{ cursor: "pointer", fontWeight: 700, bgcolor: boardScope === "friends" ? "#4F7CFF" : "rgba(120,140,200,0.15)", color: boardScope === "friends" ? "#fff" : "#5B6B8C" }} />
            </Box>
          </Box>
          {(!board || board.leaderboard?.length === 0) && (
            <Typography variant="body2" color="text.secondary">
              {boardScope === "friends" ? "Henüz arkadaşın yok — Arkadaşlar sayfasından davet et." : "Bu hafta henüz sıralama yok. Ders yaptıkça XP kazan, lige tırman!"}
            </Typography>
          )}
          {board?.leaderboard?.map(row => (
            <Box key={row.id} sx={{ display: "flex", alignItems: "center", gap: 1.2, py: 0.7, borderBottom: "1px solid rgba(120,140,200,0.1)" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, width: 28, textAlign: "center",
                color: row.rank === 1 ? "#C9A961" : row.rank === 2 ? "#9aa3b2" : row.rank === 3 ? "#cd7f32" : "#5B6B8C" }}>
                {row.rank <= 3 ? ["🥇", "🥈", "🥉"][row.rank - 1] : row.rank}
              </Typography>
              <Avatar sx={{ width: 28, height: 28, fontSize: 13, fontWeight: 700, bgcolor: "rgba(79,124,255,0.15)", color: "#4F7CFF" }}>
                {(row.name || "Ö")[0].toUpperCase()}
              </Avatar>
              <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: "#1a2238" }} noWrap>{row.name}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#8B6CFF" }}>{row.weeklyXp} XP</Typography>
            </Box>
          ))}
        </Box>
      )}

      {!data.isExam && (
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
        {/* Seviye ilerlemesi */}
        <Box sx={{ flex: 1, minWidth: 280, background: "rgba(255,255,255,0.72)", border: "1px solid rgba(120,140,200,0.16)", borderRadius: 4, p: 2.5 }}>
          <Typography fontWeight={700} sx={{ mb: 1.5, color: "#1a2238" }}>Seviye İlerlemesi</Typography>
          {(data.levelProgress || []).map(lp => (
            <Box key={lp.level} sx={{ mb: 1.3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1a2238" }}>{lp.level}</Typography>
                <Typography sx={{ fontSize: 12, color: "#5B6B8C" }}>{lp.done}/{lp.total}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {Array.from({ length: lp.total }).map((_, i) => (
                  <Box key={i} sx={{ flex: 1, height: 8, borderRadius: 4,
                    background: i < lp.done ? "linear-gradient(90deg,#4F7CFF,#8B6CFF)" : "rgba(120,140,200,0.16)" }} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Beceriler */}
        <Box sx={{ flex: 1, minWidth: 280, background: "rgba(255,255,255,0.72)", border: "1px solid rgba(120,140,200,0.16)", borderRadius: 4, p: 2.5 }}>
          <Typography fontWeight={700} sx={{ mb: 1.5, color: "#1a2238" }}>Becerilerin</Typography>
          <SkillBar label="📐 Gramer" value={data.skills?.grammar || 0} color="#8B6CFF" />
          <SkillBar label="📖 Kelime" value={data.skills?.vocabulary || 0} color="#4F7CFF" />
          <SkillBar label="🗣️ Konuşma" value={data.skills?.speaking || 0} color="#34D2E8" />
          <SkillBar label="👂 Dinleme" value={data.skills?.listening || 0} color="#10B981" />
          {data.vocab?.due > 0 && (
            <Box sx={{ mt: 1.5, p: 1.2, borderRadius: 2, bgcolor: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)" }}>
              <Typography sx={{ fontSize: 12.5, color: "#b45309", fontWeight: 600 }}>
                🔁 {data.vocab.due} kelimenin tekrar zamanı geldi — derste pekiştirilecek.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      )}

      {/* Rozetler */}
      {(data.badges || []).length > 0 && (
        <Box sx={{ mt: 2.5, background: "rgba(255,255,255,0.72)", border: "1px solid rgba(120,140,200,0.16)", borderRadius: 4, p: 2.5 }}>
          <Typography fontWeight={700} sx={{ mb: 1.5, color: "#1a2238" }}>🏅 Rozetlerin</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {data.badges.map(b => (
              <Chip key={b} icon={<EmojiEventsIcon />} label={b.replace(/_/g, " ")}
                sx={{ bgcolor: "linear-gradient(135deg,#E8CF8A,#C9A961)", background: "linear-gradient(135deg,#FCE9A8,#E8CF8A)", color: "#7a5b00", fontWeight: 700 }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Kategorili çalışma geçmişi (en son en üstte) */}
      <Box sx={{ mt: 2.5, background: "rgba(255,255,255,0.72)", border: "1px solid rgba(120,140,200,0.16)", borderRadius: 4, p: 2.5 }}>
        <Typography fontWeight={700} sx={{ mb: 1.5, color: "#1a2238" }}>📂 Çalışma Geçmişin</Typography>
        {log.length === 0 && <Typography variant="body2" color="text.secondary">Henüz işlenen konu yok — derse başla!</Typography>}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {sections.map(sec => {
            const items = log.filter(l => l.section === sec).slice(0, 8);
            const meta = SECTION_META[sec];
            return (
              <Box key={sec} sx={{ flex: 1, minWidth: 220 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: meta.color, mb: 0.8 }}>{meta.icon} {meta.label}</Typography>
                {items.length === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
                {items.map((it, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.8, py: 0.5,
                    borderBottom: "1px solid rgba(120,140,200,0.1)" }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: meta.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12.5, color: "#1a2238", textTransform: "capitalize" }} noWrap>{it.title}</Typography>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

